import { useRef } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { FormDialog } from "@shared";
import { useQuery } from "@tanstack/react-query";

import { ClientDetailResponse, ContactDetailType, clientsApi } from "@/api";
import { ClientNotificationSection } from "@/common/components/appointments/shared/components/client-notification-section/ClientNotificationSection";
import { useAppointmentFormOptions } from "@/common/components/appointments/shared/hooks/useAppointmentFormOptions";
import { useEndFollowingStart } from "@/common/components/appointments/shared/hooks/useEndFollowingStart";
import {
  ALL_COUNSELORS_OPTION_ID,
  FormikCounselorSelect,
} from "@/common/components/counselor-select/FormikCounselorSelect";
import { FormikAutocomplete } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocomplete";
import { FormikSystemDropdownAutocomplete } from "@/common/components/fields/formik/formik-autocomplete/FormikSystemDropdownAutocomplete";
import { FormikDatePicker } from "@/common/components/fields/formik/formik-date-picker/FormikDatePicker";
import FormikTimePicker from "@/common/components/fields/formik/formik-time-picker/FormikTimePicker";
import { ReadOnlyClientField } from "@/common/components/read-only-client-field/ReadOnlyClientField";
import { nameof } from "@/common/helper/commonHelper";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { ClientAppointmentFormValues, ClientAppointmentMode } from "./ClientAppointmentCreation.types";
import { firstStoredContact } from "./ClientAppointmentCreation.utils";
import { FreeSlotList } from "./components/free-slot-list/FreeSlotList";
import { useClientAppointmentCreation } from "./hooks/useClientAppointmentCreation";
import { useFreeAppointmentSlotsQuery } from "./hooks/useFreeAppointmentSlotsQuery";

interface Props {
  client: ClientDetailResponse;
  onClose: () => void;
}

const CLIENT_APPOINTMENT_DIALOG_TITLE_ID = "client-appointment-creation-dialog-title";

export function ClientAppointmentCreationDialog({ client, onClose }: Props) {
  const { t } = useTranslation();

  // The notification defaults (SMS preselected, channels disabled without contact data) depend on the
  // client's contact details, so the form only mounts once they are loaded — this keeps the formik
  // initial values final and avoids the enableReinitialize reset footgun.
  const { data: contacts, isPending } = useQuery({
    queryKey: [QueryKey.ClientPersonalData, client.clientId],
    queryFn: () => clientsApi.getClientPersonalData({ clientId: client.clientId }),
    select: (data) => ({
      firstPhoneNumber: firstStoredContact(data.contactDetails, ContactDetailType.Phone),
      firstEmail: firstStoredContact(data.contactDetails, ContactDetailType.Email),
    }),
  });

  // The dialog frame always opens — the staff member asked for it. Only its content waits on the
  // contact details: returning null here instead would make the button look dead, permanently so
  // once the failed query is cached.
  if (isPending) {
    return (
      <StatusDialog onClose={onClose}>
        <CircularProgress aria-label={t("clients.client.appointmentCreation.dialog.loadingContacts")} />
      </StatusDialog>
    );
  }

  if (!contacts) {
    return (
      <StatusDialog onClose={onClose}>
        <Alert severity="error">{t("clients.client.appointmentCreation.dialog.contactsLoadError")}</Alert>
      </StatusDialog>
    );
  }

  return (
    <ClientAppointmentCreationForm
      client={client}
      firstPhoneNumber={contacts.firstPhoneNumber}
      firstEmail={contacts.firstEmail}
      onClose={onClose}
    />
  );
}

/**
 * The dialog shell for the states that precede the form: the title and a close button, with a
 * spinner or an error in place of the fields. Deliberately not {@link FormDialog} — there is no
 * formik state yet, and offering "Termin erstellen" over a spinner would be a lie.
 */
function StatusDialog({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  const { t } = useTranslation();

  return (
    <Dialog open onClose={onClose} fullWidth aria-labelledby={CLIENT_APPOINTMENT_DIALOG_TITLE_ID}>
      <DialogTitle
        id={CLIENT_APPOINTMENT_DIALOG_TITLE_ID}
        variant="h1"
        sx={{ p: 5, pb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        {t("clients.client.appointmentCreation.dialog.title")}
        <IconButton onClick={onClose} aria-label={t("common.close")}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 5, pt: 0 }}>
        <Stack sx={{ alignItems: "center", py: 3 }}>{children}</Stack>
      </DialogContent>
    </Dialog>
  );
}

interface FormProps extends Props {
  firstPhoneNumber: string | null;
  firstEmail: string | null;
}

function ClientAppointmentCreationForm({ client, firstPhoneNumber, firstEmail, onClose }: FormProps) {
  const { t } = useTranslation();
  const { locationOptions, purposeOptions } = useAppointmentFormOptions();

  const { formik, submitDisabled } = useClientAppointmentCreation({
    clientId: client.clientId,
    assignedCounselorId: client.assignedCounselorId,
    firstPhoneNumber,
    firstEmail,
    onClose,
  });

  const { mode, purposeKey, counselorId, selectedSlotId } = formik.values;
  const {
    slots,
    isLoading: isSlotsLoading,
    isError: isSlotsError,
  } = useFreeAppointmentSlotsQuery(purposeKey, counselorId);

  // "Alle Berater:innen" is taken off the field while manual entry is shown (see handleModeChange), yet
  // the staff member expects to find it again on return — so the switch remembers it. Any change the
  // staff member makes to the field in the meantime, a pick or a clear, wins over the remembered choice.
  const restoreAllCounselorsRef = useRef(false);

  const handleModeChange = (nextMode: ClientAppointmentMode | null) => {
    if (nextMode === null) {
      return;
    }

    // Zweck, Berater:in and the notification selection live in the same formik values,
    // so they are preserved across the switch; only the mode-specific input differs.
    void formik.setFieldValue(nameof<ClientAppointmentFormValues>("mode"), nextMode);

    // The one exception: "Alle Berater:innen" only exists on the free-slots tab, because a hand-entered
    // Termin needs exactly one owner.
    if (nextMode === "manualEntry") {
      if (counselorId === ALL_COUNSELORS_OPTION_ID) {
        restoreAllCounselorsRef.current = true;
        void formik.setFieldValue(nameof<ClientAppointmentFormValues>("counselorId"), null);
      }
      return;
    }

    if (restoreAllCounselorsRef.current && counselorId === null) {
      void formik.setFieldValue(nameof<ClientAppointmentFormValues>("counselorId"), ALL_COUNSELORS_OPTION_ID);
    }
    restoreAllCounselorsRef.current = false;
  };

  const clearSelectedSlot = () =>
    void formik.setFieldValue(nameof<ClientAppointmentFormValues>("selectedSlotId"), null);

  const handleManualCounselorChange = () => {
    restoreAllCounselorsRef.current = false;
    clearSelectedSlot();
  };

  const handleStartDayChange = useEndFollowingStart(formik, "startDay", "endDay", { emptyEndDurationMinutes: 0 });
  const handleStartTimeChange = useEndFollowingStart(formik, "startTime", "endTime", { endDayField: "endDay" });

  return (
    <FormDialog
      title={t("clients.client.appointmentCreation.dialog.title")}
      onClose={onClose}
      formik={formik}
      submitLabel={t("clients.client.appointmentCreation.dialog.submitButton")}
      submitDisabled={submitDisabled}
      fullWidth
    >
      <Stack spacing={3}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          aria-label={t("clients.client.appointmentCreation.dialog.modeToggleAriaLabel")}
          onChange={(_event, next: ClientAppointmentMode | null) => handleModeChange(next)}
          sx={{
            "& .MuiToggleButton-root.Mui-selected": {
              backgroundColor: "primary.main",
              color: "primary.contrastText",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            },
          }}
        >
          <ToggleButton value="freeSlots">{t("clients.client.appointmentCreation.dialog.freeSlotsMode")}</ToggleButton>
          <ToggleButton value="manualEntry">
            {t("clients.client.appointmentCreation.dialog.manualEntryMode")}
          </ToggleButton>
        </ToggleButtonGroup>

        {mode === "freeSlots" ? (
          <Grid container rowSpacing={2} columnSpacing={2}>
            <Grid size={6}>
              <FormikSystemDropdownAutocomplete
                formik={formik}
                fieldName="purposeKey"
                label={t("clients.client.appointmentCreation.dialog.purpose")}
                required
                options={purposeOptions}
                onValueChange={clearSelectedSlot}
              />
            </Grid>
            <Grid size={6}>
              <FormikCounselorSelect
                formik={formik}
                fieldName="counselorId"
                label={t("clients.client.appointmentCreation.dialog.counselor")}
                required
                includeAllOption
                onValueChange={clearSelectedSlot}
              />
            </Grid>
            <Grid size={12}>
              <FreeSlotList
                purposeSelected={purposeKey !== ""}
                counselorSelected={counselorId !== null}
                showCounselor={counselorId === ALL_COUNSELORS_OPTION_ID}
                slots={slots}
                isLoading={isSlotsLoading}
                isError={isSlotsError}
                selectedSlotId={selectedSlotId}
                onSelect={(appointmentId) =>
                  void formik.setFieldValue(nameof<ClientAppointmentFormValues>("selectedSlotId"), appointmentId)
                }
              />
            </Grid>
          </Grid>
        ) : (
          <Grid container rowSpacing={2} columnSpacing={2}>
            <Grid size={3}>
              {/*
                The free-slot tab can only ever offer future Termine, and the backend refuses to
                attach a client to a past one — so manual entry is held to the same rule. Today
                itself stays selectable: a Termin that already happened this morning is still one
                the staff member may record.
              */}
              <FormikDatePicker
                formik={formik}
                fieldName="startDay"
                label={t("clients.client.appointmentCreation.dialog.startDate")}
                required
                disablePast
                onValueChange={handleStartDayChange}
              />
            </Grid>
            <Grid size={3}>
              <FormikTimePicker
                formik={formik}
                fieldName="startTime"
                label={t("clients.client.appointmentCreation.dialog.startTime")}
                required
                onValueChange={handleStartTimeChange}
              />
            </Grid>
            <Grid size={3}>
              <FormikDatePicker
                formik={formik}
                fieldName="endDay"
                label={t("clients.client.appointmentCreation.dialog.endDate")}
                required
                disablePast
              />
            </Grid>
            <Grid size={3}>
              <FormikTimePicker
                formik={formik}
                fieldName="endTime"
                label={t("clients.client.appointmentCreation.dialog.endTime")}
                required
              />
            </Grid>
            <Grid size={6}>
              <FormikSystemDropdownAutocomplete
                formik={formik}
                fieldName="purposeKey"
                label={t("clients.client.appointmentCreation.dialog.purpose")}
                required
                options={purposeOptions}
                onValueChange={clearSelectedSlot}
              />
            </Grid>
            <Grid size={6}>
              <FormikAutocomplete
                formik={formik}
                fieldName="location"
                freeSolo
                label={t("clients.client.appointmentCreation.dialog.location")}
                options={locationOptions}
              />
            </Grid>
            <Grid size={6}>
              <FormikCounselorSelect
                formik={formik}
                fieldName="counselorId"
                label={t("clients.client.appointmentCreation.dialog.counselor")}
                required
                onValueChange={handleManualCounselorChange}
              />
            </Grid>
          </Grid>
        )}

        {/*
          No clientId, so the field renders as plain read-only text rather than a ClientLink: this
          dialog is opened from that client's own Stammdaten tab, so a link back would navigate
          nowhere — and would trip the unsaved-changes guard on the way.
        */}
        <ReadOnlyClientField clientName={`${client.lastName}, ${client.firstName}`} />

        <ClientNotificationSection formik={formik} phone={firstPhoneNumber} email={firstEmail} cancellation={false} />
      </Stack>
    </FormDialog>
  );
}
